import { State, Selector, Action, StateContext, Store, Select } from '@ngxs/store';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';
import { SoftphoneService } from 'app/services/call/softphone.service';
import {
  InitiateCall,
  LoadUnendedCall,
  UpdateCall,
  MutateCallState,
  QueueCall,
  ClearActiveCall,
  DismissCallAlert,
  PushCallAlert,
  StartRingTone,
  EndRingTone,
  ConnectIncomingCall,
  DismissCall,
  ConnectIncomingQueueCall,
  DismissQueueCall,
  HoldCall,
  UnHoldCall,
  InitiateSecondCall,
  SwapCalls,
  MergeCalls,
  MuteState,
  TransferCall,
  HandleCallTimers,
  GetCurrentCallState,
  DisconnectCall,
  CancelCall,
  SilentMonitor,
  SilentMonitorInProgress,
  ToggleSilentMonitorMuteState,
  UpdateUnknownCall
} from './call.actions';
import { tap, catchError } from 'rxjs/operators';
import { TwilioDeviceService } from 'app/twilio-device/twilio-device.service';
import { ActiveCall, CallTimers, CallMuteStatus } from 'app/models/dto/call.model';
import { Observable, of } from 'rxjs';
import { Alert } from 'app/models/dto/alert.model';
import { Inject, Injectable } from '@angular/core';
import { LenderUserState } from '../lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import Rollbar from 'rollbar';
import { RollbarService } from 'app/rollbar';

export class CallStateModel {
  call: ActiveCall | any;
  secondaryCall: ActiveCall | any;
  dispositionCall: ActiveCall | any;
  alerts: Alert[];
  processing: boolean;
  merging: boolean;
  twilioDeviceIsLoading: boolean;
  silentMonitorInProgress: boolean;
  callTimers: CallTimers;
  mutedCall: CallMuteStatus;
}

@State<CallStateModel>({
  name: 'call',
  defaults: {
    call: null,
    secondaryCall: null,
    dispositionCall: null,
    alerts: [],
    processing: false,
    merging: false,
    twilioDeviceIsLoading: false,
    silentMonitorInProgress: false,
    callTimers: {},
    mutedCall: {},
  }
})

@Injectable()

export class CallState {

  @Selector()
  static isOnCall(state: CallStateModel): boolean {
    return !!(state.call && state.call.inProgress);
  }

  @Selector()
  static call(state: CallStateModel) {
    return state.call;
  }

  @Selector()
  static secondaryCall(state: CallStateModel) {
    return state.secondaryCall;
  }

  @Selector()
  static dispositionCall(state: CallStateModel) {
    return state.dispositionCall;
  }

  @Selector()
  static alerts(state: CallStateModel) {
    return state.alerts;
  }

  @Selector()
  static processing(state: CallStateModel) {
    return state.processing;
  }

  @Selector()
  static merging(state: CallStateModel) {
    return state.merging;
  }

  @Selector()
  static twilioDeviceIsLoading(state: CallStateModel) {
    return state.twilioDeviceIsLoading;
  }

  @Selector()
  static silentMonitorInProgress(state: CallStateModel) {
    return state.silentMonitorInProgress;
  }

  @Selector()
  static callTimers(state: CallStateModel) {
    return state.callTimers;
  }

  @Selector()
  static callMuteStatus(state: CallStateModel) {
    return state.mutedCall;
  }

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  ringToneSound = new Audio();

  private currentUser: LenderUser;

  constructor(
    private store: Store,
    private softphoneService: SoftphoneService,
    private twilioDeviceService: TwilioDeviceService,
    private eventBusService: EventBusService,
    @Inject(RollbarService) private rollbar: Rollbar
  ) {
    this.lenderUser$.subscribe((currentUser) => {
      this.currentUser = currentUser;
    });
  }

  /**
   * Start an outbound call.  This currently handles both the new softphone conferencing implementation
   * as well as the legacy softphone depending on the user's permission set.
   *
   * @param call: Call
   */
  @Action(InitiateCall)
  async initiateCall({ patchState, getState }: StateContext<CallStateModel>, { call }: InitiateCall) {
    patchState({ processing: true });
    const currentCall = getState().call;
    if (currentCall && currentCall.inProgress) {
      this.store.dispatch(new PushCallAlert('error', 'You cannot start a new call when a call is already in progress.'));
      return;
    }

    if (call.borrower) {
      patchState({ call: this.formatForState('dialing', call) });
    }

    if (await this.twilioDeviceService.isSoftphoneEnabled('outbound')) {
      patchState({ twilioDeviceIsLoading: true });
      await this.twilioDeviceService.setup().then(() => {
        patchState({ twilioDeviceIsLoading: false });
      });
    }

    let outgoingCallType: Observable<any>;

    if (call.borrowerId === -1) {
      // DIRECT DIAL
      outgoingCallType = this.softphoneService.directDialNumber(call.phone);
    } else {
      // BORROWER CALL
      outgoingCallType = this.softphoneService.postCall({ borrowerId: call.borrowerId, phone: call.phone });
    }

    return outgoingCallType.pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to initiate call. Please try again.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      const activeCall = callData.active;
      if (activeCall && callData.activeCallLegs) {
        activeCall.callLegs = callData.activeCallLegs;
      }
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const secondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      if (activeCall) {
        this.store.dispatch(new MutateCallState('dialing', activeCall));
        localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.id, disposition: activeCall.disposition }));
      }
      patchState({ secondaryCall, processing: false });
    })).subscribe();
  }

  /**
   * Once an active call is on hold, a second call can be initiated.
   *
   * @param callInfo: object containing the number to dial and the twilioCallSid
   */
  @Action(InitiateSecondCall)
  async initiateSecondCall({ patchState }: StateContext<CallStateModel>, { callInfo }: InitiateSecondCall) {
    patchState({ processing: true });

    return this.softphoneService.initiateSecondCall(callInfo['twilioNumber'], callInfo['callId']).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to initiate call. Please try again.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
      }
      const activeCall = this.formatForState('dialing', callData.active);
      activeCall.inProgress = true;
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const secondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.callId, disposition: activeCall.disposition }));
      patchState({ call: activeCall, secondaryCall, processing: false });
      this.store.dispatch(new HandleCallTimers());
      this.store.dispatch(new MuteState(null));
    })).subscribe();
  }

  /**
   * Transfer a call to another phone number.
   *
   * @param callInfo: object containing the number to dial and the twilioCallSid
   */
  @Action(TransferCall)
  async transferCall({ patchState, getState }: StateContext<CallStateModel>, { callInfo }: TransferCall) {
    patchState({ processing: true });
    const currentCall = getState().call;
    if (currentCall) {
      const callCopy = { ...currentCall };
      callCopy.callState = 'transferring';
      patchState({ call: callCopy });
      return this.softphoneService.transferCall(callInfo['twilioNumber'], callInfo['callId']).pipe(catchError(err => {
        const errorText = err.message ? err.message : 'Call could not be transferred';
        this.store.dispatch(new PushCallAlert('error', errorText));
        patchState({ processing: false, call: currentCall });
        return of();
      }), tap((callData) => {
        if (callData.active) {
          let updateState = 'connected';
          if (callData.activeCallLegs) {
            const userCallLeg = callData.activeCallLegs.find(cl => cl.lenderUserId === this.currentUser.id);
            if (userCallLeg) {
              updateState = userCallLeg.status;
            }
          }
          updateState = updateState === 'hold' ? 'on hold' : updateState;
          if (callData.active && callData.activeCallLegs) {
            callData.active.callLegs = callData.activeCallLegs;
          }
          const activeCall = this.formatForState(updateState, callData.active);
          activeCall.inProgress = true;
          localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.callId, disposition: activeCall.disposition }));
          patchState({ call: activeCall });
        } else if (!callData.active) {
          this.store.dispatch(new ClearActiveCall);
        }
        if (callData.secondary && callData.secondaryCallLegs) {
          callData.secondary.callLegs = callData.secondaryCallLegs;
        }
        const secondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
        const message = 'Call successfully transferred';
        patchState({ secondaryCall, processing: false, alerts: [ ...getState().alerts, { level: 'success', message } ] });
        this.store.dispatch(new HandleCallTimers());
        this.store.dispatch(new MuteState(null));
      })).subscribe();
    }
  }

  /**
   * New softphone feature only.
   * Changes which call is on hold and which is active.
   */
  @Action(MergeCalls)
  async mergeCalls({ patchState, getState }: StateContext<CallStateModel>, { }: MergeCalls) {
    const currentCall = { ...getState().call };
    const secondaryCall = { ...getState().secondaryCall };
    if (!currentCall || !secondaryCall) {
      this.store.dispatch(new PushCallAlert('error', 'There was a problem merging the calls.'));
      return;
    }
    if (currentCall.callId === secondaryCall.callId) {
      this.store.dispatch(new PushCallAlert('error', 'Single call given for merge.'));
      return;
    }
    if (currentCall.callState !== 'connected' &&
      currentCall.callState !== 'on hold' &&
      currentCall.callState !== 'call joined') {
      this.store.dispatch(new PushCallAlert('error', 'It does not appear that you are currently connected to an active call.'));
      return;
    }
    patchState({ processing: true, merging: true });
    return this.softphoneService.mergeCalls(currentCall.callId, secondaryCall.callId).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to merge calls. Please try again.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false, merging: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
      }
      const newActiveCall = this.formatForState('connected', callData.active);
      newActiveCall.inProgress = true;
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const newSecondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      patchState({ call: newActiveCall, secondaryCall: newSecondaryCall, processing: false, merging: false });
      this.store.dispatch(new HandleCallTimers());
      this.store.dispatch(new MuteState(null));
      localStorage.setItem('currentCall', JSON.stringify({ callId: newActiveCall.callId, disposition: newActiveCall.disposition }));
    })).subscribe();
  }

  /**
   * New softphone feature only.
   * Changes which call is on hold and which is active.
   */
  @Action(SwapCalls)
  async swapCalls({ patchState, getState }: StateContext<CallStateModel>, { }: SwapCalls) {
    const currentCall = { ...getState().call };
    const secondaryCall = { ...getState().secondaryCall };
    if (!currentCall || !secondaryCall) {
      this.store.dispatch(new PushCallAlert('error', 'There was a problem swapping out the calls.'));
      return;
    }
    if (currentCall.callId === secondaryCall.callId) {
      this.store.dispatch(new PushCallAlert('error', 'Single call given for swap.'));
      return;
    }
    if (
      currentCall.callState !== 'connected' &&
      currentCall.callState !== 'on hold' &&
      currentCall.callState !== 'call joined'
      ) {
      // A non-replicatable error has occurred with a legit active call being "on hold", thus this second check.
      this.store.dispatch(new PushCallAlert('error', 'It does not appear that you are currently connected to an active call.'));
      return;
    }
    patchState({ processing: true });
    return this.softphoneService.swapCalls(currentCall.callId, secondaryCall.callId).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to swap calls. Please try again.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
      }
      const newActiveCall = this.formatForState('connected', callData.active);
      newActiveCall.inProgress = true;
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const newSecondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      patchState({ call: newActiveCall, secondaryCall: newSecondaryCall, processing: false });
      this.store.dispatch(new HandleCallTimers());
      this.store.dispatch(new MuteState(null));
      localStorage.setItem('currentCall', JSON.stringify({ callId: newActiveCall.callId, disposition: newActiveCall.disposition }));
    })).subscribe();
  }

  /**
   * Place a call on hold.  There can only be one active call in order to use this action.
   * If there are two active calls, one can be on hold and the other would need to be muted.
   *
   * @param callId: number
   */
  @Action(HoldCall)
  holdCall({ patchState, getState }: StateContext<CallStateModel>, { callId }: HoldCall) {
    patchState({ processing: true });
    if (getState().secondaryCall) {
      this.store.dispatch(new PushCallAlert('error', 'Cannot put call on hold with a secondary call'));
      return;
    }
    return this.softphoneService.placeCallOnHold(callId).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to place the call on hold.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
      }
      const newActiveCall = this.formatForState('on hold', callData.active);
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const newSecondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      newActiveCall.inProgress = true;
      patchState({ call: newActiveCall, secondaryCall: newSecondaryCall, processing: false });
    }));
  }

  /**
   * Join User on Active call. User should be permanently muted.
   *
   * @param callId: number
   *
   */
  @Action(SilentMonitor)
  async silentMonitor({ patchState }: StateContext<CallStateModel>, { callId }: SilentMonitor) {
    patchState({ processing: true });

    if (await this.twilioDeviceService.isSoftphoneEnabled('inbound')) {
      patchState({ twilioDeviceIsLoading: true });
      await this.twilioDeviceService.setup().then(() => {
        patchState({ twilioDeviceIsLoading: false });
      });
    }

    return this.softphoneService.silentMonitorCall(callId).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to dispatch silent monitor call.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
        const newActiveCall = this.formatForState('connected', callData.active);
        newActiveCall.inProgress = true;

        patchState({ call: newActiveCall, processing: false });
        this.store.dispatch(new SilentMonitorInProgress(true));
        this.store.dispatch(new MuteState(true));
        localStorage.setItem('currentCall', JSON.stringify({ callId: newActiveCall.callId, disposition: newActiveCall.disposition }));
      } else {
        patchState({ processing: false });
      }
    })).subscribe();
  }

  /**
   * Toggle the Mute State for the Silent Monitor Participant.
   *
   * @param callId: number
   * @param muteState: boolean
   *
   */
  @Action(ToggleSilentMonitorMuteState)
  async toggleSilentMonitorMuteState({ getState, patchState }: StateContext<CallStateModel>, { muteState }: ToggleSilentMonitorMuteState) {

    const activeCallCopy = { ...getState().call };

    const setupNotComplete = !activeCallCopy ||
      !activeCallCopy.callId ||
      activeCallCopy.callState !== 'connected' ||
      !this.twilioDeviceService.connected();

    if (setupNotComplete) { 
      return; 
    }
    patchState({ processing: true });

    return this.softphoneService.toggleSilentMonitorMuteState(activeCallCopy.callId, muteState).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to dispatch silent monitor mute state toggle.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
        const newActiveCall = this.formatForState('connected', callData.active);
        newActiveCall.inProgress = true;

        patchState({ call: newActiveCall, processing: false });
        this.store.dispatch(new MuteState(muteState));
      } else {
        patchState({ processing: false });
      }
    })).subscribe();
  }

  /**
   * Place a call off hold.
   * If there are two active calls, one can be on hold and the other would need to be muted.
   *
   * @param callId: number
   */
  @Action(UnHoldCall)
  unHoldCall({ patchState }: StateContext<CallStateModel>, { callId }: UnHoldCall) {
    patchState({ processing: true });
    return this.softphoneService.takeCallOffHold(callId).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to take the call off hold.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
      }
      const newActiveCall = this.formatForState('connected', callData.active);
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const newSecondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      newActiveCall.inProgress = true;
      patchState({ call: newActiveCall, secondaryCall: newSecondaryCall, processing: false });
    }));
  }

  /**
   * On initiation of the call display, this action is called in order to maintain the active call in the UI.
   *
   * @param callId: number
   */
  @Action(LoadUnendedCall)
  loadUnendedCall(_: StateContext<CallStateModel>, { callId }: LoadUnendedCall) {
      return this.softphoneService.getCall(callId).pipe(catchError(err => {
        const errorText = err.message ? err.message : 'API Error. Failed to fetch incoming call.';
        this.store.dispatch(new PushCallAlert('error', errorText));
        return of();
      }), tap(incomingCall => {
        this.store.dispatch(new MutateCallState('disconnected', incomingCall));
      }));
  }

  /**
   * New softphone feature only.
   * Connect an incoming queue call with lenderUsers.
   *
   * @param callId: number
   */
  @Action(ConnectIncomingQueueCall)
  async connectIncomingQueueCall({ patchState, getState }: StateContext<CallStateModel>, { callId }: ConnectIncomingQueueCall) {
    this.store.dispatch(new EndRingTone());
    let errorText: string;

    const currentCall = getState().call;
    if (currentCall && (currentCall.inProgress || (currentCall.callId && !currentCall.disposition))) {
      errorText = 'You cannot accept an incoming call when another call is already in progress or call wrap up is incomplete.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      return;
    }

    if (await this.twilioDeviceService.isSoftphoneEnabled('inbound')) {
      patchState({ twilioDeviceIsLoading: true });
      await this.twilioDeviceService.setup().then(() => {
        patchState({ twilioDeviceIsLoading: false });
      });
    }

    return this.softphoneService.answerQueueCall(callId).subscribe({
      next: callData => {
        if (!callData) {
          this.twilioDeviceService.destroy();
          this.store.dispatch(new PushCallAlert('info', 'The call was no longer available, better luck next time.'));
          return;
        };
        const activeCall = callData.active || callData;
        if (activeCall && callData.activeCallLegs) {
          activeCall.callLegs = callData.activeCallLegs;
        }
        activeCall.incomingCallType = 'softphone'; // Assuming only softphone users at this juncture
        localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.id, disposition: activeCall.disposition }));
        this.eventBusService.publish(new this.eventBusService.types.InboundCallSuccessEvent);
        this.store.dispatch(new MutateCallState('connected', activeCall));
      },
      error: err => {
        this.twilioDeviceService.destroy();
        errorText = err.message ? err.message : 'The call was no longer available, better luck next time.';

        // Display expected information to the user
        const expectedInformation = [
          'This call has already ended.',
          'This call has already been claimed.',
          'The call was no longer available, better luck next time.',
          'The call was sent to voicemail.'
        ];

        // Display expected errors
        const expectedErrors = [
          'Call could not be found.',
          'User not found. You may need to log in again.',
          'You do not have access to this call.'
        ];

        if (expectedInformation.includes(errorText)) {
          this.store.dispatch(new PushCallAlert('info', errorText));
        } else if (expectedErrors.includes(errorText)) {
          this.store.dispatch(new PushCallAlert('error', errorText));
        } else {
          this.rollbar.error(`Answer Queue Call Error: ${errorText}`, err);
          // Do not display the error to the user.
          return;
        }

      }
    });

  }

  /**
   * New softphone feature only.
   * Handle and individual lenderUser's dismissal of an incoming queue call.
   *
   * @param callId: number
   * @param updates: CallUpdates
   */
  @Action(DismissQueueCall)
  dismissQueueCall(_: StateContext<CallStateModel>, { callId, updates }: DismissQueueCall) {
    this.store.dispatch(new EndRingTone());
    return this.softphoneService.dismissQueueCall(callId, updates).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'The queue call had an error during dismissal.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      return of();
    }));
  }


  /**
   * Starts the phone ringing if the user is configured for softphone
   */
  @Action(StartRingTone)
  async startRingTone({ getState }: StateContext<CallStateModel>, {}: StartRingTone) {
    const activeCall = getState().call;
    if (!activeCall || !activeCall.callId || activeCall.callState === 'disconnected') {
      this.ringTone(true);
    }
  }

  /**
   * Stops the phone from ringing
   */
  @Action(EndRingTone)
  async endRingTone( {}: EndRingTone) {
    this.ringTone(false);
  }

  /**
   * @param callId: number
   * @param connectSoftPhone: boolean
   */
  @Action(ConnectIncomingCall)
  async connectIncomingCall({ patchState, getState }:
     StateContext<CallStateModel>, { callId, connectSoftPhone }: ConnectIncomingCall) {
    this.store.dispatch(new EndRingTone());
    let errorText: string;
    let endpointCall: Observable<any>;

    const currentCall = getState().call;
    if (currentCall && (currentCall.inProgress || (currentCall.callId && !currentCall.disposition))) {
      errorText = 'You cannot accept an incoming call when another call is already in progress or call wrap up is incomplete.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      return;
    }

    if (connectSoftPhone && await this.twilioDeviceService.isSoftphoneEnabled('inbound')) {
      patchState({ twilioDeviceIsLoading: true });
      await this.twilioDeviceService.setup().then(() => {
        patchState({ twilioDeviceIsLoading: false });
      });
    }

    // ORDER OF THIS CHECK IS IMPORTANT. NEEDS TO BE MOST UNIQUE TO LEAST.
    if (connectSoftPhone) {
      errorText = 'API Error. Failed to connect enqueued incoming call.';
      endpointCall = this.softphoneService.answerCall(callId);
    } else {
      errorText = 'API Error. Failed to fetch incoming call.';
      endpointCall = this.softphoneService.getCall(callId);
    }

    return endpointCall.pipe(catchError(err => {
      if (err.message) {
        errorText = err.message;
      }
      this.store.dispatch(new PushCallAlert('error', errorText));
      return of();
    }), tap(callData => {
          const activeCall = callData.active || callData;
          if (activeCall && callData.activeCallLegs) {
            activeCall.callLegs = callData.activeCallLegs;
          }
          activeCall.incomingCallType = connectSoftPhone ? 'softphone' : 'general';
          const newSecondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
          patchState({ secondaryCall: newSecondaryCall });
          localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.id, disposition: activeCall.disposition }));
          this.eventBusService.publish(new this.eventBusService.types.InboundCallSuccessEvent);
          this.store.dispatch(new MutateCallState('connected', activeCall));
        })
      ).subscribe();
  }

  /**
   * Used in store-downgrade file.
   *
   * @param borrowerId: number
   */
    @Action(UpdateUnknownCall)
    updateUnknownCall({ getState }: StateContext<CallStateModel>, { borrowerId }: UpdateUnknownCall) {
      const activeCall = getState().call;
      if (!activeCall.borrowerId || activeCall.borrowerId === -1) {
        return this.softphoneService.addToExistingBusiness(activeCall.callId, borrowerId).pipe(catchError(err => {
          const errorText = err.message ? err.message : 'API Error. Failed to update call to new borrower.';
          this.store.dispatch(new PushCallAlert('error', errorText));
          return of();
        }));
      }
    }

  /**
   * Used to update a call object via the attributes available in the UpdateCall model.
   *
   * @param updates: UpdateCall
   */
  @Action(UpdateCall)
  updateCall({ patchState, getState }: StateContext<CallStateModel>, { updates }: UpdateCall) {
    const activeCall = getState().call;
    const dispositionCall = getState().dispositionCall;
    const silentMonitorInProgress = getState().silentMonitorInProgress;
    if (activeCall && activeCall.callId) {
      if (updates.end) {
        this.store.dispatch(new DisconnectCall(activeCall.callId));
        return;
      }
      const useDispostion = updates.disposition && dispositionCall && dispositionCall.callId;
      const updateId = updates.dispositionId ? updates.dispositionId : useDispostion ? dispositionCall.callId : activeCall.callId;
      if (updates.dispositionId) {
        delete updates.dispositionId;
      }
      return this.softphoneService.updateCall(updateId, updates).pipe(catchError(err => {
        const errorText = err.message ? err.message : 'API Error. Failed to update call.';
        this.store.dispatch(new PushCallAlert('error', errorText));
        return of();
      }), tap(updatedCall => {
        if (updatedCall.disposition === updates.disposition) {
          patchState({ dispositionCall: null });
        }
        if (updates.disposition && updates.disposition === 'Wrong number.') {
          if (activeCall.borrowerId) {
            this.flagAsWrongNumber(activeCall.borrowerId, activeCall.toNumber);
          }
        }
        if (!silentMonitorInProgress) {
          const needsDisposition = updates.end &&
            activeCall.callState !== 'disconnected' &&
            !/^(conference|internal|add) call/i.exec(<string> updatedCall.disposition);
          if (needsDisposition) {
            patchState({ dispositionCall: updatedCall});
            if (!updatedCall.borrower) {
              updatedCall.borrower = activeCall.borrower;
            }
            this.store.dispatch(new MutateCallState('disconnected', updatedCall));
          }
        }
      }));
    }
  }

  /**
   * Allows a lenderUser to dismiss an incoming call.
   *
   * @param callId: number
   * @param updates: CallUpdates
   */
  @Action(DismissCall)
  dismissCall(_: StateContext<CallStateModel>, { callId, updates }: DismissCall) {
    this.store.dispatch(new EndRingTone());
    return this.softphoneService.dismissCall(callId, updates).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'An error occurred while dismissing the call.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      return of();
    }));
  }

  @Action(DisconnectCall)
  disconnectCall({ getState, patchState }: StateContext<CallStateModel>, { callId }: DisconnectCall) {
    const currentCall = getState().call;
    const dispositionCall = getState().dispositionCall;
    const silentMonitorInProgress = getState().silentMonitorInProgress;
    if (currentCall && currentCall.callState !== 'canceled') {
      const callCopy = { ...currentCall};
      callCopy.callState = 'disconnected';
      patchState({ call: callCopy });
    }
    if (!silentMonitorInProgress) {
      const needsDisposition = !dispositionCall &&
        currentCall &&
        currentCall.callState !== 'canceled' &&
        !/^(conference|internal|add) call/i.exec(<string> currentCall.disposition);
      if (needsDisposition) {
        patchState({ dispositionCall: currentCall });
      }
    }
    patchState({ processing: true });
    return this.softphoneService.disconnectCall(callId).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'An error occurred while disconnecting the call.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      return of();
    }), tap(callData => {
      if (!callData) {
        return;
      }
      if (callData.active) {
        let updateState = 'connected';
        if (callData.activeCallLegs) {
          const userCallLeg = callData.activeCallLegs.find(cl => cl.lenderUserId === this.currentUser.id);
          if (userCallLeg) {
            updateState = userCallLeg.status;
          }
        }
        updateState = updateState === 'hold' ? 'on hold' : updateState;
        if (callData.active && callData.activeCallLegs) {
          callData.active.callLegs = callData.activeCallLegs;
        }
        const activeCall = this.formatForState(updateState, callData.active);
        activeCall.inProgress = true;
        localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.callId, disposition: activeCall.disposition }));
        patchState({ call: activeCall });
      } else if (!callData.active && currentCall.callState === 'canceled') {
        patchState({ dispositionCall: null });
        this.store.dispatch(new ClearActiveCall);
      }
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const secondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      patchState({ secondaryCall, processing: false });
      this.store.dispatch(new HandleCallTimers());
      this.store.dispatch(new MuteState(null));
      this.store.dispatch(new SilentMonitorInProgress(false));
    }));
  }

  @Action(CancelCall)
  cancelCall({ getState, patchState }: StateContext<CallStateModel>, {}: CancelCall) {
    const activeCall = getState().call;
    if (activeCall) {
      const callCopy = { ...activeCall };
      callCopy.callState = 'canceled';
      patchState({ call: callCopy });
      this.store.dispatch(new DisconnectCall(activeCall.callId));
    } else {
      this.store.dispatch(new PushCallAlert('error', 'No active call for cancel.'));
    }
    return;
  }

  @Action(QueueCall)
  queueCall({ patchState }: StateContext<CallStateModel>, { queuedCall }: QueueCall) {
    setTimeout(() => {
      patchState({ call: queuedCall });
    }, 0);
  }

  @Action(SilentMonitorInProgress)
  silentMonitorInProgress({ patchState }: StateContext<CallStateModel>, { active }: SilentMonitorInProgress) {
    patchState({ silentMonitorInProgress: active });
  }

  @Action(ClearActiveCall)
  clearActiveCall({ patchState }: StateContext<CallStateModel>, {}: ClearActiveCall) {
    patchState({ call: null });
    localStorage.removeItem('currentCall');
  }

  @Action(GetCurrentCallState)
  getCurrentCallState({ patchState, getState }: StateContext<CallStateModel>, { passedState }: GetCurrentCallState) {
    patchState({ processing: true });
    const currentCall = getState().call;
    const secondaryCall = getState().secondaryCall;
    const silentMonitorInProgress = getState().silentMonitorInProgress;
    const needsDispositionModal =
      passedState === 'disconnected' &&
      currentCall &&
      currentCall.callState !== 'canceled' &&
      currentCall.callId &&
      !/^(conference|internal|add) call/i.exec(<string> currentCall.disposition) &&
      !secondaryCall &&
      !silentMonitorInProgress;

    if (needsDispositionModal) {
      patchState({ dispositionCall: currentCall });
    }
    return this.softphoneService.getCurrentCallState().pipe(catchError(err => {
      const errorText = err.message ? err.message : 'Failed to get lender user call state.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      patchState({ processing: false });
      return of();
    }), tap(callData => {
      let updateState = passedState ? passedState : currentCall ? currentCall.callState : 'connected';
      if (callData.active === null  && silentMonitorInProgress) {
        this.store.dispatch(new SilentMonitorInProgress(false));
        patchState({ call: callData.active });
      }
      if (callData.activeCallLegs) {
        const someCallLegsOnHold = callData.activeCallLegs.some(cl => cl.status === 'hold');
        // This could be problematic if we start putting individual callLegs on hold
        if (someCallLegsOnHold) {
          updateState = 'on hold';
        }

        if (passedState === 'call joined') {
          const silentMonitorCallLeg = callData.activeCallLegs.find(cl => cl.label === 'silentMonitor');

          if (silentMonitorCallLeg) {
            if (silentMonitorCallLeg.lender_user.id !== this.currentUser.id) {
              const fullName = silentMonitorCallLeg.lender_user.user.first + ' ' + silentMonitorCallLeg.lender_user.user.last;
              this.store.dispatch(new PushCallAlert('info', fullName + ' has joined the call muted.'));
            }
            if (updateState !== 'on hold') {
              //Only update state if no one is on hold
              updateState = 'connected';
            }
          }
        }
      }
      if (callData.active && callData.activeCallLegs) {
        callData.active.callLegs = callData.activeCallLegs;
      }
      if (callData.secondary && callData.secondaryCallLegs) {
        callData.secondary.callLegs = callData.secondaryCallLegs;
      }
      const newSecondaryCall = callData.secondary ? this.formatForState('on hold', callData.secondary) : null;
      patchState({ secondaryCall: newSecondaryCall });
      const processingOnHoldHangup = (callData.active?.id === currentCall?.callId) && secondaryCall && !newSecondaryCall;
      if (processingOnHoldHangup) {
        updateState = currentCall ? currentCall.callState : 'connected';
        this.store.dispatch(new UpdateCall({ disposition: 'External hang up on hold.', dispositionId: secondaryCall.callId}));
      }
      const activeCall = callData.active ? this.formatForState(updateState, callData.active) : null;
      if (activeCall) {
        activeCall.inProgress = true;
        localStorage.setItem('currentCall', JSON.stringify({ callId: activeCall.callId, disposition: activeCall.disposition }));
        patchState({ call: activeCall });
      } else {
        localStorage.removeItem('currentCall');
        if (currentCall && /^(internal|externally transferred) call/i.exec(<string> currentCall.disposition)) {
          this.store.dispatch(new ClearActiveCall);
        }
      }
      this.store.dispatch(new HandleCallTimers());
      this.store.dispatch(new MuteState(null));
      patchState({ processing: false });
    })).subscribe();
  }

  /**
   * Place a call on/off mute.
   *
   * @param setting: boolean
   */
  @Action(MuteState)
  muteToggle({ patchState, getState }: StateContext<CallStateModel>, { setting }: MuteState) {
    const activeCallCopy = { ...getState().call };
    const callMuteCopy = { ...getState().mutedCall };

    const setupNotComplete = !activeCallCopy ||
      !activeCallCopy.callId ||
      activeCallCopy.callState !== 'connected' ||
      !this.twilioDeviceService.connected();
    if (setupNotComplete) { return; }

    patchState({ processing: true });
    if (setting === null) {
      setting = callMuteCopy[activeCallCopy.callId] ?? false;
    }

    this.twilioDeviceService.muteCall(setting);
    const twilioDeviceMuteStatus = this.twilioDeviceService.isTwilioDeviceMuted();

    // Check that the twilio device updated
    if (twilioDeviceMuteStatus === setting) {
      callMuteCopy[activeCallCopy.callId] = twilioDeviceMuteStatus;
      patchState({ mutedCall: callMuteCopy });
    } else {
      this.store.dispatch(new PushCallAlert('error', 'The mute button had a problem...'));
    }
    patchState({ processing: false });
  }

  /**
   * Reusable action to modify the call state.
   *
   * @param state: string
   * @param callData: any
   */
  @Action(MutateCallState)
  mutateCallState({ getState, patchState }: StateContext<CallStateModel>, { state, callData }: MutateCallState) {
    const stateCall = getState().call;
    const activeCallCopy = { ...stateCall };
    if (callData && callData.requestState) {
      this.store.dispatch(new GetCurrentCallState(state));
      return;
    }

    // MAKE SURE CHANGE AND ACTIVE CALL BOTH AVAILABLE
    const activeCallAndChangesExist = !callData && (!stateCall || !activeCallCopy.callId);
    if (activeCallAndChangesExist) { return; }

    if (getState().merging && callData && callData.callStatus === 'completed') {
      return;
    }

    // CALL ENDED EVENT HANDLING
    const callHasEnded = state === 'disconnected' && callData.callStatus && callData.callStatus !== 'completed';
    if (callHasEnded) {
      this.store.dispatch(new ClearActiveCall());
      return;
    }

    // MAKE SURE CHANGES ARE STARTING AN ACTIVE CALL OR MATCH CURRENT ACTIVE CALL
    const initialActiveCall = (!stateCall || !activeCallCopy.callId) && !!callData;
    const callChangesMatchActiveCall = (callData.callId && callData.callId === activeCallCopy.callId) ||
      (callData.borrowerId && callData.borrowerId === activeCallCopy.borrowerId);
    if (initialActiveCall || callChangesMatchActiveCall) {
      let activeCall: ActiveCall;

      const acceptedWithoutBorrower = !callData.borrower &&
      (callData.type === 'incoming' || /direct dial|add call|internal call/i.exec(callData.disposition));
      if (callData.borrower || acceptedWithoutBorrower) { // UPDATE WITH CALL WHOLE CALLDATA OBJECT
        activeCall = this.formatForState(state, callData);
      } else { // ONLY UPDATE CALL.CALLSTATE
        activeCall = activeCallCopy;
        activeCall.callState = state;
      }

      // PROCESSES FOR IN PROGRESS CALLS
      const inProgressCallStates = state === 'dialing' || state === 'connected' || state === 'on hold' || state === 'call joined';
      if (inProgressCallStates) {
        activeCall.inProgress = true;
      } else {
        activeCall.inProgress = false;
      }
      // PATCH STATE WITH UPDATES
      patchState({ call: activeCall });
    }

    // Secondary

    // FOLLOW UP ACTIONS AFTER CALL STATE PATCH
    this.store.dispatch(new HandleCallTimers());
    this.store.dispatch(new MuteState(null));
  }

  @Action(HandleCallTimers)
  handleCallTimers({ getState, patchState }: StateContext<CallStateModel>, {}: HandleCallTimers) {
    const currentCall = getState().call;
    const callTimersCopy = { ...getState().callTimers };
    if (currentCall && currentCall.inProgress && currentCall.callId && !callTimersCopy[currentCall.callId]) {
      callTimersCopy[currentCall.callId] = Date.now();
      patchState({ callTimers: callTimersCopy });
    }
  }

  @Action(PushCallAlert)
  pushCallAlert({ getState, patchState }: StateContext<CallStateModel>, { alertLevel, message }: PushCallAlert) {
    patchState({ alerts: [ ...getState().alerts, { level: alertLevel, message } ]});
  }

  @Action(DismissCallAlert)
  dismissCallAlert({ getState, patchState }: StateContext<CallStateModel>, { alertIndex }: DismissCallAlert) {
    const alerts = [ ...getState().alerts ];
    alerts.splice(alertIndex, 1);
    patchState({ alerts });
  }

  /**
   * Update a borrower's record to indicate that a particular phone number on their account is not their phone number.
   *
   * @param borrowerId: number
   * @param toNumber: string
   */
  private flagAsWrongNumber(borrowerId: number, toNumber: string) {
    this.softphoneService.flagAsWrongNumber(borrowerId, toNumber).pipe(catchError(err => {
      const errorText = err.message ? err.message : 'API Error. Failed to flag wrong number.';
      this.store.dispatch(new PushCallAlert('error', errorText));
      return of();
    })).subscribe();
  }

  private ringTone(ring: boolean): any {
    if (!this.ringToneSound) {
      return;
    }
    if (ring && !this.ringToneSound.paused) {
      // Already playing audio
      return;
    }
    if (ring && this.ringToneSound.paused) {
      this.ringToneSound.src = 'https://cdn.lendio.com/audio-files/IncomingCallRingtone.mp3';
      this.ringToneSound.volume = 0.5;
      this.ringToneSound.load();
      this.ringToneSound.muted = false;
      this.ringToneSound.loop = true;
      this.ringToneSound.play().catch(() => {
        // do nothing if error from playing audio
      });
    } else {
      this.ringToneSound.pause();
      this.ringToneSound.muted = true;
      this.ringToneSound.currentTime = 0;
    }
  }

  /**
   * Reusable function to format the call object for state object(s)
   *
   * @param callState: string
   * @param response: any
   * @returns call: ActiveCall
   */
  private formatForState(callState: string, response: any): ActiveCall {
    let displayName = response.borrower ? response.borrower.name : 'Unknown Caller';
    let displayToNumber = response.toNumber;

    const setInternal = response && /^internal call/i.exec(<string> response.disposition);
    const setConference = response && /^conference call/i.exec(<string> response.disposition);
    const userAvailable = response.lender_user && response.lender_user.id !== this.currentUser.id;
    if (setInternal && userAvailable) {
      displayName = response.lender_user.fullName;
    }
    if (setInternal && response.callLegs) {
      const receivingLenderUser = response.callLegs.find(cl => cl.lenderUserId && cl.lenderUserId !== this.currentUser.id);
      displayName = receivingLenderUser ? receivingLenderUser.lender_user.fullName : displayName;
      displayToNumber = receivingLenderUser ? receivingLenderUser.callerNumber : displayToNumber;
    }
    if (setConference && response.callLegs) {
      const borrower = response.callLegs.find(cl => cl.borrowerId);
      const receivingLenderUser = response.callLegs.find(cl => cl.lenderUserId && cl.lenderUserId !== this.currentUser.id);
      const unknownCaller = response.callLegs.find(cl => cl.lenderUserId === null && cl.borrowerId === null);

      if (borrower) {
        displayName = borrower.borrower.name;
        displayToNumber = borrower.callerNumber;
      }

      if (!borrower && receivingLenderUser) {
        displayName = receivingLenderUser ? receivingLenderUser.lender_user.fullName : displayName;
        displayToNumber = receivingLenderUser ? receivingLenderUser.callerNumber : displayToNumber;
      } 

      if (!borrower && !receivingLenderUser && unknownCaller) {
        displayName = 'Unknown Caller';
        displayToNumber = unknownCaller.callerNumber;
      }
    }

    const call: ActiveCall = {
      callId: response.id,
      activityId: response.activityId,
      borrowerId: response.borrowerId,
      lenderId: response.lenderId,
      callLegs: response.callLegs,
      created: response.created,
      createdTimestamp: response.createdTimestamp,
      fromNumber: response.fromNumber,
      toNumber: displayToNumber,
      lenderUserExtension: response.lenderUserExtension,
      twilioAccountSid: response.twilioAccountSid,
      twilioCallSid: response.twilioCallSid,
      twilioNumber: response.twilioNumber,
      type: response.type,
      businessName: displayName,
      borrower: response.borrower,
      disposition: response.disposition,
      callState
    };

    if (response.borrower) {
      if (response.borrower.primary_contact) {
        call.borrower = response.borrower;
        call.officePhone = response.borrower.primary_contact.workPhone ? response.borrower.primary_contact.workPhone : null;
        call.mobilePhone = response.borrower.primary_contact.mobilePhone ? response.borrower.primary_contact.mobilePhone : null;
      }
        call.businessPhone = response.borrower.phone ? response.borrower.phone : null;
    }

    if (response.type === 'incoming') {
      call.incomingCallType = response.incomingCallType;
    }

    return call;
  }

}
